import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachStar from "./wrapper/EachStar";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class Star extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleDrawStar = this.handleDrawStar.bind(this);
    this.handleEndDrawStar = this.handleEndDrawStar.bind(this);
    this.handleDragStar = this.handleDragStar.bind(this);
    this.handleDragStarComplete = this.handleDragStarComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("stars").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragStar(index, newValues) {
    this.setState({
      override: {
        index,
        ...newValues,
      },
    });
  }

  handleDragStarComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { stars } = this.props;
      const newStars = stars.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              x: override.x,
              y: override.y,
              sx: override.sx !== undefined ? override.sx : each.sx,
              sy: override.sy !== undefined ? override.sy : each.sy,
              angle: override.angle !== undefined ? override.angle : each.angle,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newStars, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState(
        {
          current: {
            ...current,
            x2: xyValue[0],
            y2: xyValue[1],
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    } else {
      this.setState(
        {
          current: {
            x: xyValue[0],
            y: xyValue[1],
            x2: null,
            y2: null,
            sx: 1,
            sy: 1,
            angle: 0,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDrawStar(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleEndDrawStar(xyValue, moreProps, e) {
    const { stars, appearance } = this.props;
    const { current } = this.state;

    if (!isDefined(current) || !isDefined(current.x)) {
      return;
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    // Calculate scale based on drag distance
    const x2 = xyValue[0];
    const y2 = xyValue[1];

    // Default scale if just clicked (no drag)
    let sx = 1;
    let sy = 1;
    let angle = 0;

    // If dragged, calculate scale from pixel distance
    if (current.x2 !== null && current.y2 !== null) {
      const centerX = xScale(current.x);
      const centerY = yScale(current.y);
      const x2Pixel = xScale(x2);
      const y2Pixel = yScale(y2);

      const dx = x2Pixel - centerX;
      const dy = y2Pixel - centerY;

      // CRITICAL: Use same formula as resize handle - baseDim = width, divide by (baseDim / 2)
      const distance = Math.sqrt(dx * dx + dy * dy);
      const baseDim = 12; // dimension[0] - must match helper method
      if (distance > 5) {
        sx = Math.max(0.2, distance / (baseDim / 2));
        sy = sx; // Keep proportional initially
      }
    }

    const newStars = [
      ...stars.map((d) => ({ ...d, selected: false })),
      {
        x: current.x,
        y: current.y,
        // Don't store x2/y2 - they're only for initial drawing
        sx,
        sy,
        angle,
        selected: true,
        appearance,
      },
    ];

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newStars, moreProps, e);
      },
    );
  }

  render() {
    const { appearance, stars, hoverText } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionStrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { current, override } = this.state;

    const tempStar =
      isDefined(current) && isDefined(current.x) ? (
        <EachStar
          interactive={false}
          x={current.x}
          y={current.y}
          x2={current.x2}
          y2={current.y2}
          sx={current.sx}
          sy={current.sy}
          angle={current.angle}
          stroke={
            appearance.stroke && typeof appearance.stroke === "object"
              ? appearance.stroke
              : {
                  color: appearance.stroke || "#FFD700",
                  width: 2,
                  opacity: 1,
                  dash: "Solid",
                }
          }
          fill={
            appearance.fill && typeof appearance.fill === "object"
              ? appearance.fill
              : { color: appearance.fill || "#FFD700", opacity: 0.6 }
          }
        />
      ) : null;

    return (
      <g>
        {stars.map((each, idx) => {
          const eachAppearance = {
            stroke: each.appearance?.stroke ||
              appearance.stroke || {
                color: "#FFD700",
                width: 2,
                opacity: 1,
                dash: "Solid",
              },
            fill: each.appearance?.fill ||
              appearance.fill || { color: "#FFD700", opacity: 0.6 },
          };

          return (
            <EachStar
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...(idx === override?.index ? override : each)}
              stroke={eachAppearance.stroke}
              fill={eachAppearance.fill}
              onDrag={this.handleDragStar}
              onDragComplete={this.handleDragStarComplete}
            />
          );
        })}
        {tempStar}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionStrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEndDrawStar}
          onMouseMove={this.handleDrawStar}
          data-evo-id="interactive-star"
        />
      </g>
    );
  }
}

Star.propTypes = {
  enabled: PropTypes.bool.isRequired,

  snap: PropTypes.bool.isRequired,
  shouldDisableSnap: PropTypes.func.isRequired,
  snapTo: PropTypes.func,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionStrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    fill: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
  }).isRequired,

  stars: PropTypes.array.isRequired,

  hoverText: PropTypes.object.isRequired,
};

Star.defaultProps = {
  snap: false,
  shouldDisableSnap: () => true,
  snapTo: null,

  onStart: noop,
  onComplete: noop,

  currentPositionStroke: "#FFD700",
  currentPositionOpacity: 1,
  currentPositionStrokeWidth: 2,
  currentPositionRadius: 4,

  stars: [],

  hoverText: {
    enable: false,
    selectedText: "",
    text: "Clique e arraste para criar uma estrela",
    bgHeight: "auto",
    bgWidth: "auto",
  },
};

export default Star;
